import { useDispatch, useSelector } from "react-redux";
import { refreshPriceApparat } from "../../store/actions/paymentsActions";
import useLivePrintModes from "../print-settings/useLivePrintModes";
import ConfiguredWidgets from "./widgets/ConfiguredWidgets";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import WeatherSlide from "./widgets/WeatherSlide";
import ExchangeRateSlide from "./widgets/ExchangeRateSlide";
import CountdownSlide from "./widgets/CountdownSlide";
import TopSocialFlipCard from "./widgets/TopSocialFlipCard";
import AdvertisementModal from "../ads/AdvertisementModal";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { endKioskSession } from "../../services/kioskServiceSession";
import { useAdPlaylist } from "../ads/AdsSync";
import { PLACEMENTS } from "../ads/adPlaylist";

// Контакт поддержки из панели (KioskSettings.support): кого звать на месте.
function SupportStrip({ support, lang, t }) {
    if (!support || (!support.phone && !support.telegram && !support.text?.[lang])) return null;
    const telegram = support.telegram ? `@${support.telegram.replace(/^@/, "")}` : null;
    return (
        <div className="flex-none mt-[1.6vh] rounded-[2vh] bg-white px-[2vw] py-[1.2vh] flex items-center gap-[1.6vw] text-[1.6vh] text-[#3A4150] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.06)]">
            <span className="font-bold text-[#246CE2] flex-none">{t("apparatPolicy.support_title")}</span>
            {support.text?.[lang] && <span className="min-w-0 truncate">{support.text[lang]}</span>}
            <span className="ml-auto flex-none flex items-center gap-[1.6vw] font-bold">
                {support.phone && <span>☎ {support.phone}</span>}
                {telegram && <span>✈ {telegram}</span>}
            </span>
        </div>
    );
}

// Animation variants for the bento tiles
const tileVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: i * 0.15 },
    }),
};

// Main RouteList Component
function RouteList() {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const priceApparat = useSelector(state => state.payments.priceApparat);
    const config = priceApparat?.kiosk_settings;
    const defaultLanguage = config?.default_language ?? "kk";
    useLivePrintModes();
    useEffect(() => {
        if (localStorage.getItem("deviceToken")) dispatch(refreshPriceApparat());
    }, [dispatch]);
    const [showAdModal, setShowAdModal] = useState(false);
    // Реклама и настройки заставки приходят из AdsSync (App.js) — он держит их
    // свежими всё время работы киоска: они нужны и экрану печати.
    const { items: idleAds, settings: adSettings } = useAdPlaylist(PLACEMENTS.idle);
    const inactivityTime = adSettings.idle_delay_sec * 1000;
    const hasIdleAds = idleAds.length > 0;
    const inactivityTimer = useRef(null);

    // Очистка истории прохода: на главной странице сбрасываем позицию в стеке истории,
    // чтобы из неё нельзя было «Назад» вернуться в сессию предыдущего пользователя.
    useEffect(() => {
        if ((window.history.state?.idx ?? 0) > 0) {
            window.history.replaceState(
                { ...window.history.state, idx: 0 },
                "",
            );
        }
    }, []);

    // Новый сеанс использует язык аппарата. Эффект также учитывает первый
    // ответ API, но повторный опрос с тем же языком не сбрасывает выбор клиента.
    useEffect(() => {
        if (i18n.language !== defaultLanguage) {
            i18n.changeLanguage(defaultLanguage);
        }
    }, [i18n, defaultLanguage]);

    // Таймер простоя: пауза до заставки задаётся в панели для каждого аппарата.
    // Раньше таймер жил в let внутри тела компонента и терялся при каждом
    // рендере — clearTimeout гасил уже не тот таймер.
    const resetTimer = useCallback(() => {
        clearTimeout(inactivityTimer.current);
        if (location.pathname !== "/" || showAdModal || !hasIdleAds) return;
        inactivityTimer.current = setTimeout(() => setShowAdModal(true), inactivityTime);
    }, [location.pathname, showAdModal, hasIdleAds, inactivityTime]);

    useEffect(() => {
        const events = ["mousemove", "keydown", "touchstart", "scroll"];
        events.forEach((event) => window.addEventListener(event, resetTimer));
        resetTimer();
        return () => {
            clearTimeout(inactivityTimer.current);
            events.forEach((event) => window.removeEventListener(event, resetTimer));
        };
    }, [resetTimer]);

    // Главная — граница пользовательского сеанса. Сервисный токен также
    // отзывается, чтобы следующий человек у экрана не унаследовал права.
    useEffect(() => {
        endKioskSession();
    }, []);

    // Close modal and reset timer
    const handleCloseModal = useCallback(() => {
        setShowAdModal(false);
    }, []);

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col bg-[#EDEFF4]">
            <div
                className="flex-1 min-h-0 grid grid-cols-8 grid-rows-[1fr_1fr_0.6fr_0.6fr] gap-[1.6vh]"
            >
                {/* Главная CTA-плитка — распечатка */}
                <motion.div
                    className="col-span-6 row-span-2 min-h-0 min-w-0"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                >
                    <Link data-analytics="routelist.routeList.main_title.1"
                        to="/printing"
                        className="w-full h-full rounded-[3vh] bg-[#246CE2] text-white p-[4vh_3vw] flex flex-col justify-between relative overflow-hidden"
                    >
                        <div className="w-[7vh] h-[7vh] rounded-[1.6vh] bg-white/15 flex items-center justify-center">
                            <svg
                                className="w-[3.8vh] h-[3.8vh]"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="#fcfcfc"
                                    d="M8.616 20q-.667 0-1.141-.475T7 18.386V16H5.192q-.666 0-1.14-.475t-.475-1.14v-3.77q0-.85.577-1.424q.577-.576 1.423-.576h12.846q.85 0 1.425.576t.575 1.424v3.77q0 .666-.474 1.14T18.808 16H17v2.385q0 .666-.475 1.14t-1.14.475zM17 7.846H7V6.227q0-.667.475-1.14q.474-.472 1.14-.472h6.77q.666 0 1.14.475q.475.475.475 1.14zm.616 4.27q.425 0 .712-.288t.288-.712t-.288-.713t-.712-.288t-.713.288t-.287.713t.287.712t.713.288m-9 6.884h6.769q.269 0 .442-.173t.173-.442v-3.308q0-.27-.173-.442q-.173-.173-.442-.173h-6.77q-.269 0-.442.172q-.173.174-.173.443v3.308q0 .269.173.442t.443.173"
                                />
                            </svg>
                        </div>
                        <div className="pr-[9vw]">
                            <h2 className="text-[4.6vh] font-extrabold tracking-tight mb-[0.8vh]">
                                {t("routeList.main_title")}
                            </h2>
                            <p className="text-[1.9vh] text-white/75 font-medium leading-snug">
                                {t("routeList.main_subtitle")}
                            </p>
                        </div>
                        <motion.div
                            className="absolute right-[3vw] bottom-[3vh] w-[6vh] h-[6vh] rounded-full bg-white flex items-center justify-center"
                            animate={{ x: ["0vw", "1.4vw", "0vw"] }}
                            transition={{
                                duration: 2.4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <svg
                                className="w-[2.6vh] h-[2.6vh]"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#246CE2"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M5 12h14M13 6l6 6-6 6" />
                            </svg>
                        </motion.div>
                    </Link>
                </motion.div>

                {config?.widgets != null ? <ConfiguredWidgets widgets={config.widgets} /> : <>
                {/* Погода */}
                <motion.div
                    className="col-span-2"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                >
                    <WeatherSlide />
                </motion.div>

                {/* Курс USD/KZT */}
                <motion.div
                    className="col-span-2"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                >
                    <ExchangeRateSlide />
                </motion.div>

                {/* Топ пользователей / Соцсети — карточка с флипом */}
                <motion.div
                    className="col-span-4 row-span-2"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={2}
                >
                    <TopSocialFlipCard />
                </motion.div>

                {/* До сессии */}
                <motion.div
                    className="col-span-4"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={2}
                >
                    <CountdownSlide />
                </motion.div>

                {/* Место под рекламу */}
                <motion.div
                    className="col-span-4"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={3}
                >
                    <Link data-analytics="routelist.routeList.ad_placeholder.2"
                        to="/advertisement"
                        className="w-full h-full rounded-[3vh] border-[0.3vh] border-dashed border-[#C3C8D2] flex flex-row items-center justify-center gap-[1vw] text-[#9AA1AD]"
                    >
                        <svg
                            className="w-[2.4vh] h-[2.4vh]"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        >
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                        <span className="text-[1.7vh] font-bold">
                            {t("routeList.ad_placeholder")}
                        </span>
                    </Link>
                </motion.div>
                </>}
            </div>

            <SupportStrip support={config?.support} lang={i18n.language} t={t} />

            {/* Заставка при простое */}
            <AnimatePresence>
                {showAdModal && hasIdleAds && (
                    <AdvertisementModal key="screensaver" onClose={handleCloseModal} />
                )}
            </AnimatePresence>
        </div>
    );
}

export default RouteList;
